import { Mic, MicOff, AlertTriangle, Users, Clock } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import type { UseVoiceSessionReturn } from '../hooks/useVoiceSession';

interface VoiceSessionIndicatorProps {
  voice: UseVoiceSessionReturn;
  /** If true, show "Open mapping" button in popover (GM-only UI) */
  isGm: boolean;
  /** Click handler for "Open mapping" button — opens SpeakerMappingModal */
  onOpenMapping?: () => void;
}

/**
 * Compact indicator showing Discord voice bot status in the header.
 *
 * States:
 * - disconnected: gray mic-off icon, no popover content beyond status
 * - connecting: amber pulsing mic icon
 * - connected: green mic icon + STT duration badge; popover shows guild/channel/mappings/remaining-minutes
 * - stt-unavailable: red alert icon; popover explains STT provider error
 * - error: red alert icon; popover shows last error
 *
 * Also surfaces STT warning threshold (3.5h) via a separate badge.
 */
export function VoiceSessionIndicator({ voice, isGm, onOpenMapping }: VoiceSessionIndicatorProps) {
  const { status, info, warningMessage, dismissWarning } = voice;

  // Don't render anything for brand-new sessions that never had a voice connection
  if (status === 'disconnected' && !info) return null;

  const icon = iconForStatus(status);
  const variant = badgeVariantForStatus(status);
  const label = labelForStatus(status);

  const minutesUsed = info ? Math.round(info.totalSttDurationSec / 60) : 0;
  const MAX_MIN = 240; // 4 hours
  const minutesRemaining = Math.max(0, MAX_MIN - minutesUsed);

  return (
    <div className="flex items-center gap-2">
      {/* Warning banner (dismissable, shown when STT approaches limit) */}
      {warningMessage && (
        <button
          type="button"
          onClick={dismissWarning}
          className="flex items-center gap-1.5 rounded-md bg-warn/15 px-2 py-1 text-meta text-warn hover:bg-warn/25 transition-colors"
          title="Нажмите чтобы скрыть"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{warningMessage}</span>
        </button>
      )}

      {/* Main status pill with popover */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 px-2"
            aria-label={`Voice: ${label}`}
          >
            {icon}
            <Badge variant={variant} className="h-4 px-1.5 text-label font-medium">
              {label}
            </Badge>
            {status === 'connected' && (
              <span className="text-meta text-muted-foreground tabular-nums">
                {minutesUsed}м
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72">
          <div className="space-y-3">
            <div>
              <div className="text-ui font-medium">Discord Voice</div>
              <div className="text-meta text-muted-foreground">
                {descriptionForStatus(status)}
              </div>
            </div>

            {info && status === 'connected' && (
              <div className="space-y-2 border-t pt-2">
                <div className="flex items-center gap-2 text-meta">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>STT использовано: <b className="tabular-nums">{minutesUsed} мин</b></span>
                </div>
                <div className="flex items-center gap-2 text-meta">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Осталось до лимита: <b className="tabular-nums">{minutesRemaining} мин</b></span>
                </div>
                <div className="flex items-center gap-2 text-meta">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>
                    Маппинги: <b>{info.speakerMappings.length}</b> участник(ов)
                  </span>
                </div>
              </div>
            )}

            {isGm && status === 'connected' && onOpenMapping && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={onOpenMapping}
              >
                Настроить маппинг говорящих
              </Button>
            )}

            {status === 'stt-unavailable' && (
              <div className="rounded-md bg-danger/15 p-2 text-meta text-danger border-t">
                STT провайдер временно недоступен. Бот остаётся в канале, транскрипция возобновится автоматически.
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ── Helpers ──

function iconForStatus(status: string) {
  const base = 'h-4 w-4';
  switch (status) {
    case 'connected':
      return <Mic className={`${base} text-ok`} />;
    case 'connecting':
      return <Mic className={`${base} text-warn animate-pulse`} />;
    case 'stt-unavailable':
    case 'error':
      return <AlertTriangle className={`${base} text-danger`} />;
    case 'disconnected':
    default:
      return <MicOff className={`${base} text-muted-foreground`} />;
  }
}

function badgeVariantForStatus(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'connected':
      return 'default';
    case 'connecting':
      return 'secondary';
    case 'stt-unavailable':
    case 'error':
      return 'destructive';
    default:
      return 'outline';
  }
}

function labelForStatus(status: string): string {
  switch (status) {
    case 'connected': return 'В канале';
    case 'connecting': return 'Подключение';
    case 'disconnected': return 'Отключён';
    case 'stt-unavailable': return 'STT недоступен';
    case 'error': return 'Ошибка';
    default: return status;
  }
}

function descriptionForStatus(status: string): string {
  switch (status) {
    case 'connected':
      return 'Бот в голосовом канале Discord, речь транскрибируется для Тии.';
    case 'connecting':
      return 'Подключение к голосовому каналу...';
    case 'disconnected':
      return 'Бот не подключён. Используйте /roll1 connect в Discord для активации.';
    case 'stt-unavailable':
      return 'STT провайдер временно недоступен.';
    case 'error':
      return 'Ошибка голосового подключения.';
    default:
      return status;
  }
}
